import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useUpdateClientMutation } from 'src/redux/organization/organization.endpoints';
import { Client } from 'src/pages/client-management/types/client.types';
import { Dropdown } from 'primereact/dropdown';

const GRAPH_FIELD_LOADING_MS = 2500;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * PrimeReact dropdown panels are portaled to `document.body`; wheel events bubble to the sheet.
 * Only hook the scrollable items wrapper — not panel/list — so clicks/taps on options still work.
 * Do not use onTouchMove + stopPropagation here; it can block touch selection.
 */
const graphMetricDropdownPt = {
  wrapper: {
    onWheel: (e: React.WheelEvent) => e.stopPropagation(),
  },
};

const formatPlatformName = (platform: string | undefined): string => {
  if (!platform || platform === 'Default') return 'Default';
  return platform
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface CustomizeReporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

export const AVAILABLE_METRICS = [
  { label: 'Clicks/Views/Interactions', value: 'clicks', graphable: true },
  { label: 'Impressions', value: 'impressions', graphable: true },
  { label: 'Reach', value: 'reach', graphable: true },
  { label: 'Results', value: 'results', graphable: true },
  { label: 'Conversions', value: 'conversions', graphable: true },
  { label: 'CTR', value: 'ctr', graphable: true },
  { label: 'Cost', value: 'spend', graphable: true },
  { label: 'CPA', value: 'cpa', graphable: true },
  { label: 'CPM', value: 'cpm', graphable: true },
  { label: 'Video Completion Rate', value: 'video_completion_rate', graphable: true },
  { label: 'Audio Completion Rate', value: 'audio_completion_rate', graphable: true },
  { label: 'Top Zip Codes', value: 'geo', graphable: false },
  { label: 'Top Domains', value: 'domain', graphable: false },
  { label: 'Top Apps', value: 'app', graphable: false },
  { label: 'Top DMAs', value: 'dma', graphable: false },
  { label: 'Hourly Impressions', value: 'hourly', graphable: false },
  { label: 'AWP Completes', value: 'awp_completes', graphable: false },
  { label: 'Conversions by Beacon', value: 'conversions_by_beacon', graphable: false },
];

type GraphKey = 'graph_one' | 'graph_two' | 'graph_three';

interface ManageClientViewsTempProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

const ManageClientViewsTemp: React.FC<ManageClientViewsTempProps> = ({ client }) => {
  const [currentAccount, setCurrentAccount] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [localClient, setLocalClient] = useState<Client>(client);
  const [loadingGraphNumber, setLoadingGraphNumber] = useState<1 | 2 | 3 | null>(null);
  const [updateClient] = useUpdateClientMutation();

  useEffect(() => {
    setLocalClient(client);
  }, [client]);

  useEffect(() => {
    if (client?.views?.length > 0 && !currentAccount) {
      const defaultView = localClient.views.find(view => view.account_ext_id === 'default');
      if (defaultView) {
        setCurrentAccount(defaultView.account_ext_id);
      }
    }
  }, [localClient?.views, currentAccount]);

  const currentView = localClient?.views?.find(view => view.account_ext_id === currentAccount);

  const handleShowDifferenceToggle = async() => {
    if (!currentView) return;

    const showDifference = !currentView.show_difference;

    const updatedViews = localClient.views.map(view =>
      view.account_ext_id === currentAccount
        ? { ...view, show_difference: showDifference }
        : view
    );

    const updatedClient = {
      ...localClient,
      views: updatedViews,
    };

    setSaveStatus('saving');
    try {
      await updateClient({
        clientId: localClient.ext_client_id,
        data: updatedClient,
      });

      setLocalClient(updatedClient);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update metrics:', error);
      setSaveStatus('idle');
    }
  };

  const handleMetricToggle = async(metric: string) => {
    if (!currentView) return;

    const newMetrics = currentView.metrics.includes(metric)
      ? currentView.metrics.filter(m => m !== metric)
      : [...currentView.metrics, metric];

    const updatedViews = localClient.views.map(view =>
      view.account_ext_id === currentAccount
        ? { ...view, metrics: newMetrics }
        : view
    );

    const updatedClient = {
      ...localClient,
      views: updatedViews,
    };

    setSaveStatus('saving');
    try {
      await updateClient({
        clientId: localClient.ext_client_id,
        data: updatedClient,
      });
      setLocalClient(updatedClient);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update metrics:', error);
      setSaveStatus('idle');
    }
  };

  const handleGraphMetricChange = async(
    graphNumber: 1 | 2 | 3,
    metricType: 'metric_one' | 'metric_two',
    value: string | null | { label: string; value: string | null }
  ) => {
    if (!currentView) return;

    // Defensive: If value is an object, extract .value
    const actualValue = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;

    const graphKey = `graph_${graphNumber === 1 ? 'one' : graphNumber === 2 ? 'two' : 'three'}` as GraphKey;
    const updatedViews = localClient.views.map(view =>
      view.account_ext_id === currentAccount
        ? {
          ...view,
          [graphKey]: {
            ...view[graphKey],
            [metricType]: actualValue,
          },
        }
        : view
    );

    const updatedClient = {
      ...localClient,
      views: updatedViews,
    };

    setSaveStatus('saving');
    setLoadingGraphNumber(graphNumber);
    try {
      await Promise.all([
        updateClient({
          clientId: localClient.ext_client_id,
          data: updatedClient,
        }).unwrap(),
        delay(GRAPH_FIELD_LOADING_MS),
      ]);
      setLocalClient(updatedClient);
      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update graph metrics:', error);
      setSaveStatus('idle');
    } finally {
      setLoadingGraphNumber(null);
    }
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full">
      <span className="text-lg font-medium">
        Step 2: Customize Campaign Reporter
      </span>
      <div className="flex items-center gap-2">
        {saveStatus === 'saving' && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saved
          </span>
        )}
      </div>
    </div>
  );

  // Provide context to ReportsTab so components can be wrapped dynamically
  return (
    <div className="flex flex-col gap-8 md:flex-row">
      {/* Accounts Column */}
      <div className="w-full shrink-0 md:w-[220px]">
        <h3 className="mb-3 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
          Accounts
        </h3>
        <div className="space-y-1">
          {localClient?.views?.map((view, index) => {
            const noMetrics = view.metrics.length === 0;
            const allGraphsEmpty =
                  (!view.graph_one.metric_one && !view.graph_one.metric_two) &&
                  (!view.graph_two.metric_one && !view.graph_two.metric_two) &&
                  (!view.graph_three.metric_one && !view.graph_three.metric_two);
            const isSelected = currentAccount === view.account_ext_id;
            return (
              <button
                key={`${view.account_ext_id}_${index}`}
                onClick={() => setCurrentAccount(view.account_ext_id)}
                className={`flex w-full items-center justify-between rounded-lg px-3.5 py-3 text-left text-[13.5px] ${
                  isSelected
                    ? 'bg-[#EFF6FF] font-semibold text-[#2563EB]'
                    : 'text-[#0D0F1A] hover:bg-[#F0F1F5]'
                }`}
              >
                <span className={noMetrics && allGraphsEmpty ? 'opacity-40' : ''}>
                  {view.account_ext_id === 'default' ? view.account_name : `${view.account_name} (${formatPlatformName(view.platform)})`}
                </span>
                {isSelected ? (
                  <span className="text-[#2563EB]">
                    ›
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Configuration Column */}
      <div className="flex-1 min-h-0 md:w-2/3 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-2 md:px-4">
          {/* Warning message for empty configuration */}
          {currentView && (() => {
            const noMetrics = currentView.metrics.length === 0;
            const allGraphsEmpty =
                (!currentView.graph_one.metric_one && !currentView.graph_one.metric_two) &&
                (!currentView.graph_two.metric_one && !currentView.graph_two.metric_two) &&
                (!currentView.graph_three.metric_one && !currentView.graph_three.metric_two);
            if (noMetrics && allGraphsEmpty) {
              return (
                <div className="flex items-center gap-2">
                  <i className="pi pi-info-circle" />
                  <span className="text-black/50">
                    <span>
                      Warning! No metrics configured for this account —
                      {' '}
                    </span>
                    <button
                      onClick={() => setCurrentAccount('default')}
                      className="underline hover:text-blue-600"
                    >
                      account defaults
                    </button>
                    <span>
                      {' '}
                      will apply.
                    </span>
                  </span>
                </div>
              );
            }
            return null;
          })()}
          {currentView && (
            <div className="space-y-4">
              {/* Metric Cards Section */}
              <div>
                <h3 className="mb-3 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
                  Metric Cards
                </h3>

                <label
                  className="mb-4 flex items-center gap-2.5 text-[13.5px] text-[#0D0F1A]"
                >
                  <input
                    type="checkbox"
                    checked={currentView.show_difference}
                    onChange={() => handleShowDifferenceToggle()}
                    className="size-4 rounded border-[#D9DBE3] accent-[#2563EB]"
                  />
                  <span>
                    Show Difference on Metric Cards
                  </span>
                </label>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {AVAILABLE_METRICS.map(metric => (
                    <label
                      key={metric.value}
                      className={`flex cursor-pointer items-center gap-2.5 rounded-[10px] border-[1.5px] px-4 py-3.5 text-[13px] font-medium ${
                        currentView.metrics.includes(metric.value)
                          ? 'border-[#EFF6FF] bg-[#EFF6FF]'
                          : 'border-[#EDEEF2] bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={currentView.metrics.includes(metric.value)}
                        onChange={() => handleMetricToggle(metric.value)}
                        className="size-4 rounded border-[#D9DBE3] accent-[#2563EB]"
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {metric.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-xs md:text-sm text-gray-500 mt-3">
                Metrics with zero data will automatically be hidden from the Campaign Reporter dashboard.
              </p>

              {/* Graph Sections */}
              <div>
                <h3 className="text-base md:text-lg font-medium py-4">
                  Graphs
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-2 md:gap-x-3 gap-y-2 md:gap-y-0">
                  {[1, 2, 3].map(graphNum => {
                    const graphKey = `graph_${graphNum === 1 ? 'one' : graphNum === 2 ? 'two' : 'three'}` as GraphKey;
                    const isUnselected = !currentView[graphKey].metric_one && !currentView[graphKey].metric_two;
                    return (
                      <div
                        key={graphNum}
                        className={`relative border rounded-lg p-2 md:p-4 ${
                          (typeof currentView[graphKey].metric_one === 'string' || typeof currentView[graphKey].metric_two === 'string')
                            ? 'bg-blue-100 border-blue-200'
                            : ''
                        }`}
                      >
                        {loadingGraphNumber === graphNum && (
                          <div
                            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/75 backdrop-blur-[1px]"
                            aria-busy
                            aria-label="Updating graph"
                          >
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                            <span className="text-xs font-medium text-gray-600">
                              Updating…
                            </span>
                          </div>
                        )}
                        <h4 className="text-base md:text-lg font-medium mb-2 md:mb-4">
                          Graph
                          {' '}
                          {graphNum}
                        </h4>
                        <div className="space-y-2 md:space-y-4">
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                              First Metric
                            </label>
                            <Dropdown
                              inputId={`client-view-graph-${graphNum}-metric-one`}
                              appendTo={typeof document !== 'undefined' ? document.body : undefined}
                              pt={graphMetricDropdownPt}
                              value={currentView[graphKey].metric_one ?? null}
                              options={[
                                { label: 'None', value: null },
                                ...AVAILABLE_METRICS.filter(metric => metric.graphable),
                              ]}
                              optionLabel="label"
                              optionValue="value"
                              onChange={(e) => handleGraphMetricChange(graphNum as 1 | 2 | 3, 'metric_one', e.value)}
                              placeholder="None"
                              className={`w-full ${isUnselected ? 'bg-gray-200' : ''}`}
                              panelClassName="max-h-[min(280px,50vh)] [&_.p-dropdown-items-wrapper]:overscroll-y-contain"
                              panelStyle={{ zIndex: 11000 }}
                            />
                          </div>
                          <div>
                            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                              Second Metric
                            </label>
                            <Dropdown
                              inputId={`client-view-graph-${graphNum}-metric-two`}
                              appendTo={typeof document !== 'undefined' ? document.body : undefined}
                              pt={graphMetricDropdownPt}
                              value={currentView[graphKey].metric_two ?? null}
                              options={[
                                { label: 'None', value: null },
                                ...AVAILABLE_METRICS.filter(metric => metric.graphable),
                              ]}
                              optionLabel="label"
                              optionValue="value"
                              onChange={(e) => handleGraphMetricChange(graphNum as 1 | 2 | 3, 'metric_two', e.value)}
                              placeholder="None"
                              className={`w-full ${isUnselected ? 'bg-gray-200' : ''}`}
                              panelClassName="max-h-[min(280px,50vh)] [&_.p-dropdown-items-wrapper]:overscroll-y-contain"
                              panelStyle={{ zIndex: 11000 }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs md:text-sm text-gray-500 mt-3">
                  Graphs with no metrics set will automatically be hidden from the Campaign Reporter dashboard.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageClientViewsTemp;
